import * as React from "react";
import { RiSearchLine, RiInformationLine } from "@remixicon/react";
import { cn } from "../../../lib/utils";

export interface TextFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /** Label text shown above input when focused/populated */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Supporting/helper text below the input */
  supportingText?: string;
  /** Error state */
  error?: boolean;
  /** Error message (shown in supporting text area) */
  errorMessage?: string;
  /** Show leading icon */
  leadingIcon?: boolean;
  /** Custom leading icon */
  customLeadingIcon?: React.ReactNode;
  /** Show trailing icon */
  trailingIcon?: boolean;
  /** Custom trailing icon */
  customTrailingIcon?: React.ReactNode;
  /** Container className */
  containerClassName?: string;
  /** Input wrapper className */
  wrapperClassName?: string;
  /** Callback when value changes (alternative to onChange, useful for cmdk integration) */
  onValueChange?: (value: string) => void;
}

/**
 * TextField - A reusable input component with support for:
 * - Leading/trailing icons
 * - Label that floats on focus
 * - Supporting text
 * - Error state
 *
 * Uses capsule-preset color tokens.
 */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      placeholder,
      supportingText,
      error = false,
      errorMessage,
      leadingIcon = false,
      customLeadingIcon,
      trailingIcon = false,
      customTrailingIcon,
      containerClassName,
      wrapperClassName,
      className,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onValueChange,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value || defaultValue),
    );

    // Update hasValue when controlled value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(value));
      }
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(Boolean(e.target.value));
      props.onChange?.(e);
      onValueChange?.(e.target.value);
    };

    const showFloatingLabel = label && (isFocused || hasValue);
    const displaySupportingText =
      error && errorMessage ? errorMessage : supportingText;

    return (
      <div className={cn("flex w-full flex-col gap-1", containerClassName)}>
        {/* Input wrapper */}
        <div
          className={cn(
            // Base styles
            "relative flex h-12 w-full items-center gap-2 rounded-lg px-3 py-2",
            "bg-bg-primary transition-colors",
            // Border styles based on state - using capsule-preset colors
            // Note: border-solid is required because host app reset.css sets border: 0 which resets border-style
            error
              ? "border-[1.5px] border-solid border-stroke-critical"
              : isFocused
                ? "border border-solid border-stroke-primary"
                : "border-[1.5px] border-solid border-stroke-secondary hover:border-stroke-primary",
            // Disabled state
            disabled && "cursor-not-allowed bg-bg-disabled opacity-50",
            wrapperClassName,
          )}
        >
          {/* Leading icon */}
          {leadingIcon && (
            <div className="shrink-0">
              {customLeadingIcon || (
                <RiSearchLine
                  size={20}
                  className={cn(
                    "text-icon-secondary",
                    isFocused && "text-icon-primary",
                    error && "text-icon-critical",
                  )}
                />
              )}
            </div>
          )}

          {/* Input container with floating label */}
          <div className="relative flex flex-1 flex-col justify-center">
            {/* Floating label */}
            {showFloatingLabel && (
              <span
                className={cn(
                  "text-xs uppercase leading-4",
                  error ? "text-text-critical" : "text-text-tertiary",
                )}
              >
                {label}
              </span>
            )}

            {/* Input */}
            <input
              ref={ref}
              value={value}
              defaultValue={defaultValue}
              disabled={disabled}
              placeholder={showFloatingLabel ? undefined : placeholder || label}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              className={cn(
                "w-full bg-transparent outline-none",
                "text-sm leading-5 text-text-primary",
                "placeholder:text-text-tertiary",
                disabled && "cursor-not-allowed",
                className,
              )}
              {...props}
            />
          </div>

          {/* Trailing icon */}
          {trailingIcon && (
            <div className="shrink-0">
              {customTrailingIcon || (
                <RiInformationLine
                  size={24}
                  className={cn(
                    "text-icon-secondary",
                    error && "text-icon-critical",
                  )}
                />
              )}
            </div>
          )}
        </div>

        {/* Supporting text */}
        {displaySupportingText && (
          <p
            className={cn(
              "px-4 text-xs leading-4",
              error ? "text-text-critical" : "text-text-secondary",
            )}
          >
            {displaySupportingText}
          </p>
        )}
      </div>
    );
  },
);

TextField.displayName = "TextField";

export { TextField };
